'use client';

import { useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import { CUSTOMERS_QUERY } from '@paeffgen/react/crm-documents/data-access-graphql';

export interface CustomerEntry {
  customerNumber: string;
  customerName: string;
}

/**
 * Debitoren-Liste für den Admin-Cross-Login-Picker.
 *
 * Quelle ist die dedizierte `customers`-Query (DISTINCT über alle Belege) —
 * nicht mehr eine Ableitung aus den 100 neuesten Belegen. Dadurch erscheinen
 * auch Debitoren, deren jüngster Beleg älter ist. Die Sortierung erfolgt
 * locale-aware nach Name (deutsche Umlaut-Reihenfolge), die Suche filtert über
 * Nummer und Name.
 */
export function useCustomerSuggestions(query: string) {
  const { data, loading } = useQuery<{ customers: CustomerEntry[] }>(
    CUSTOMERS_QUERY,
    {
      variables: { allCustomers: true },
      fetchPolicy: 'cache-first',
    },
  );

  const customers = useMemo<CustomerEntry[]>(() => {
    if (!data?.customers) return [];
    return [...data.customers].sort((a, b) =>
      a.customerName.localeCompare(b.customerName, 'de'),
    );
  }, [data]);

  const suggestions = useMemo(() => {
    if (!query.trim()) return customers;
    const q = query.toLowerCase();
    return customers.filter(
      (c) =>
        c.customerNumber.toLowerCase().includes(q) ||
        c.customerName.toLowerCase().includes(q),
    );
  }, [customers, query]);

  return { customers, suggestions, loading };
}
